'use client';

import * as React from 'react';
import Image from 'next/image';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { cn } from '@/components/ui/utils';
import { stripHtml } from '@/lib/homepage-mappers';
import { getStrapiFormatUrl } from '@/lib/strapi-media-url';
import type { CompanyProfileLogoGridSection } from '@/types/company-profile';

gsap.registerPlugin(ScrollTrigger);

type LogoItem = {
  id: number;
  title: string;
  imageUrl: string | null;
  href: string | null;
};

// Pseudo-varied float timing so every card is at a different phase
function floatStyle(index: number): React.CSSProperties {
  const duration = 2.6 + ((index * 0.23) % 1.2); // 2.6 – 3.8 s
  const delay = (index * 0.37) % 1.8; // 0 – 1.8 s
  return {
    animationDuration: `${duration.toFixed(2)}s`,
    animationDelay: `${delay.toFixed(2)}s`,
  };
}

// ─── Logo card ─────────────────────────────────────────────────────────────────
function LogoCard({ logo }: { logo: LogoItem }) {
  const hasImage = Boolean(logo.imageUrl?.trim());

  const inner = (
    <div
      data-logo-card
      className={cn(
        'group relative flex aspect-[2/1] w-full items-center justify-center overflow-hidden',
        'rounded-2xl bg-[#f5f5f5]',
        'transition-all duration-300 ease-out',
        'hover:bg-white hover:-translate-y-1 hover:shadow-[0_12px_32px_-6px_rgba(0,0,0,0.12)]',
      )}
    >
      {/* Red bottom accent line slides in */}
      <span
        aria-hidden
        className="absolute bottom-0 left-0 h-[3px] w-full origin-left scale-x-0 rounded-full bg-[#ed1c22] transition-transform duration-300 ease-out group-hover:scale-x-100"
      />

      {hasImage ? (
        <Image
          src={logo.imageUrl!}
          alt={logo.title}
          width={110}
          height={44}
          className="max-h-[42px] w-auto max-w-[68%] object-contain"
        />
      ) : (
        <span className="px-3 text-center text-[11px] font-bold uppercase tracking-[0.1em] text-black/55 transition-colors duration-200 group-hover:text-black/80">
          {logo.title}
        </span>
      )}
    </div>
  );

  if (logo.href) {
    return (
      <a href={logo.href} target="_blank" rel="noopener noreferrer" aria-label={logo.title}>
        {inner}
      </a>
    );
  }
  return inner;
}

// ─── Section ───────────────────────────────────────────────────────────────────
interface CompanyProfileAccreditationsProps {
  zone: CompanyProfileLogoGridSection;
}

export default function CompanyProfileAccreditations({ zone }: CompanyProfileAccreditationsProps) {
  const sectionRef = React.useRef<HTMLElement>(null);
  const ruleRef = React.useRef<HTMLSpanElement>(null);
  const gridWrapRef = React.useRef<HTMLDivElement>(null);
  const gridRef = React.useRef<HTMLDivElement>(null);

  const items: LogoItem[] = (zone.logoGrid ?? [])
    .map((item) => ({
      id: item.id,
      title: item.heading?.trim() || item.media?.alternativeText?.trim() || '',
      imageUrl: getStrapiFormatUrl(item.media),
      href: item.urlOptional ?? null,
    }))
    .filter((item) => item.title || item.imageUrl);

  // ── rAF-lerp mouse parallax (no GSAP in event handlers) ───────────────────
  const rafId = React.useRef<number | null>(null);
  const targetRot = React.useRef({ rx: 0, ry: 0 });
  const curRot = React.useRef({ rx: 0, ry: 0 });
  const hovering = React.useRef(false);

  const tick = React.useCallback(() => {
    const grid = gridRef.current;
    if (!grid) return;
    const t = 0.07;
    curRot.current.rx += (targetRot.current.rx - curRot.current.rx) * t;
    curRot.current.ry += (targetRot.current.ry - curRot.current.ry) * t;
    grid.style.transform = `rotateX(${curRot.current.rx.toFixed(3)}deg) rotateY(${curRot.current.ry.toFixed(3)}deg)`;
    const still =
      Math.abs(curRot.current.rx - targetRot.current.rx) < 0.01 &&
      Math.abs(curRot.current.ry - targetRot.current.ry) < 0.01;
    if (!still || hovering.current) {
      rafId.current = requestAnimationFrame(tick);
    } else {
      rafId.current = null;
    }
  }, []);

  const onMouseMove = React.useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const el = gridWrapRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      targetRot.current.rx = ((e.clientY - r.top) / r.height - 0.5) * -8;
      targetRot.current.ry = ((e.clientX - r.left) / r.width - 0.5) * 8;
      if (!hovering.current) {
        hovering.current = true;
        rafId.current = requestAnimationFrame(tick);
      }
    },
    [tick],
  );

  const onMouseLeave = React.useCallback(() => {
    targetRot.current = { rx: 0, ry: 0 };
    hovering.current = false;
    if (!rafId.current) rafId.current = requestAnimationFrame(tick);
  }, [tick]);

  React.useEffect(
    () => () => {
      if (rafId.current) cancelAnimationFrame(rafId.current);
    },
    [],
  );

  // ── Scroll entrance (GSAP only inside useLayoutEffect) ────────────────────
  React.useLayoutEffect(() => {
    const section = sectionRef.current;
    const rule = ruleRef.current;
    if (!section) return;

    const prefersReduced =
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (prefersReduced) {
      section
        .querySelectorAll('[data-acc-line],[data-acc-fade],[data-logo-card]')
        .forEach((el) => gsap.set(el, { clearProps: 'all' }));
      if (rule) gsap.set(rule, { scaleX: 1 });
      return;
    }

    const ctx = gsap.context(() => {
      const logoCards = gsap.utils.toArray<HTMLElement>('[data-logo-card]');

      gsap.set(logoCards, { autoAlpha: 0, y: 18, scale: 0.88 });

      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: section,
          start: 'top 78%',
          toggleActions: 'play none none none',
        },
      });

      // Eyebrow
      tl.fromTo(
        section.querySelectorAll('[data-acc-fade="eyebrow"]'),
        { autoAlpha: 0, y: 10 },
        { autoAlpha: 1, y: 0, duration: 0.45, ease: 'power2.out' },
      );

      // Heading clip reveal
      tl.fromTo(
        section.querySelectorAll('[data-acc-line]'),
        { y: '108%' },
        { y: '0%', stagger: 0.1, duration: 0.78, ease: 'power3.out' },
        '-=0.22',
      );

      // Red rule
      if (rule) {
        tl.fromTo(
          rule,
          { scaleX: 0, transformOrigin: 'left center' },
          { scaleX: 1, duration: 0.6, ease: 'power2.inOut' },
          '-=0.38',
        );
      }

      // Sub copy
      tl.fromTo(
        section.querySelectorAll('[data-acc-fade="sub"]'),
        { autoAlpha: 0, y: 10 },
        { autoAlpha: 1, y: 0, duration: 0.45, ease: 'power2.out' },
        '-=0.3',
      );

      const tiles = gsap.utils.toArray<HTMLElement>('[data-float-card]');
      const rows = tiles.reduce<HTMLElement[][]>((groups, tile) => {
        const rowTop = Math.round(tile.offsetTop);
        const row = groups.find((group) => Math.abs(Math.round(group[0].offsetTop) - rowTop) < 4);

        if (row) {
          row.push(tile);
        } else {
          groups.push([tile]);
        }

        return groups;
      }, []);

      rows.forEach((row) => {
        const rowCards = row
          .map((tile) => tile.querySelector<HTMLElement>('[data-logo-card]'))
          .filter((card): card is HTMLElement => Boolean(card));

        if (!rowCards.length) return;

        gsap.to(rowCards, {
          autoAlpha: 1,
          y: 0,
          scale: 1,
          duration: 0.7,
          stagger: 0.07,
          ease: 'power3.out',
          clearProps: 'transform',
          scrollTrigger: {
            trigger: row[0],
            start: 'top 86%',
            toggleActions: 'play none none none',
          },
        });
      });
    }, section);

    return () => ctx.revert();
  }, []);

  if (items.length === 0) return null;

  return (
    <>
      {/* CSS float keyframes injected once */}
      <style>{`
        @keyframes acc-float {
          0%, 100% { transform: translateY(0px);  }
          50%       { transform: translateY(-8px); }
        }
        [data-float-card] {
          animation-name: acc-float;
          animation-timing-function: ease-in-out;
          animation-iteration-count: infinite;
          animation-fill-mode: both;
        }
        @media (max-width: 767px) {
          [data-float-card] {
            animation: none;
          }
        }
        @media (min-width: 768px) {
          [data-float-card]:hover {
            animation-play-state: paused;
          }
        }
      `}</style>

      <section ref={sectionRef} className="relative overflow-hidden bg-white">
        {/* Faint watermark */}
        <span
          aria-hidden
          className="pointer-events-none absolute -bottom-4 right-0 select-none text-[22vw] font-black uppercase leading-none tracking-tighter text-black/[0.022]"
        >
          TRUSTED
        </span>

        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          {/* ── Header ─────────────────────────────────────── */}
          <div className="mb-8 sm:mb-10">
            {zone.eyebrowHeading ? (
              <div
                data-acc-fade="eyebrow"
                className="mb-5 inline-flex items-center gap-3"
                style={{ opacity: 0 }}
              >
                <span className="h-px w-6 bg-[#ed1c22]" aria-hidden />
                <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[#ed1c22]">
                  {zone.eyebrowHeading}
                </p>
              </div>
            ) : null}

            {zone.heading ? (
              <h2 className="text-[1.65rem] font-bold leading-[1.08] tracking-tight text-black sm:text-[2.6rem] md:text-[3.1rem] lg:text-[3.6rem]">
                <span className="block overflow-hidden pb-[5px]">
                  <span className="block" data-acc-line>
                    {zone.heading}
                  </span>
                </span>
              </h2>
            ) : null}

            <span
              ref={ruleRef}
              aria-hidden
              className="mt-5 block h-[3px] w-14 rounded-full bg-[#ed1c22]"
              style={{ transform: 'scaleX(0)', transformOrigin: 'left center' }}
            />

            {zone.subHeading ? (
              <p
                data-acc-fade="sub"
                className="mt-5 max-w-[500px] text-sm leading-relaxed text-black/40 sm:text-[15px]"
                style={{ opacity: 0 }}
              >
                {stripHtml(zone.subHeading)}
              </p>
            ) : null}
          </div>

          {/* ── Logo wall ──────────────────────────────────── */}
          <div
            ref={gridWrapRef}
            style={{ perspective: '1100px' }}
            onMouseMove={onMouseMove}
            onMouseLeave={onMouseLeave}
          >
            <div
              ref={gridRef}
              className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 sm:gap-3 md:gap-4 lg:grid-cols-5 xl:grid-cols-6"
            >
              {items.map((logo, i) => (
                <div key={logo.id} data-float-card style={floatStyle(i)}>
                  <LogoCard logo={logo} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
